import type { LeadListItem } from '@groundly/shared';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { CircleCheck } from 'lucide-react';
import { useContext } from 'react';

import { useLeads } from '../../../../api/conversations.js';
import { usePermissions } from '../../../../api/me.js';
import { Button } from '../../../../components/Button.js';
import { DangerAlert } from '../../../../components/DangerAlert.js';
import styles from '../../../../components/leads.module.css';
import { PageHeading } from '../../../../components/PageHeading.js';
import { Pagination } from '../../../../components/Pagination.js';
import { LoadingArea } from '../../../../components/PlaceholderPage.js';
import { PermissionDeniedPage, WebsiteNotFoundPage } from '../../../../components/SectionStates.js';
import { StatusBadge } from '../../../../components/StatusBadge.js';
import { WebsiteStatusContext } from '../../../../components/WebsiteSection.js';
import { formatFullDateTime, leadSourceLabel } from '../../../../conversations/display.js';
import { parseConversationsSearch, searchForPage } from '../../../../conversations/search.js';

/**
 * `/websites/:websiteId/leads`: the website's leads, newest first
 * (`product/business-rules.md`, Leads). A visitor who left their details in
 * the chat's lead form shows them, for the team to get back to; one who only
 * tapped a contact route shows how. Each links to its conversation. Needs the
 * conversations permission, as the conversations do.
 */
export const Route = createFileRoute('/_app/websites/$websiteId/leads')({
  validateSearch: parseConversationsSearch,
  component: LeadsRoute,
});

const TITLE = 'Leads';

function LeadsRoute() {
  const permissions = usePermissions();
  const websiteStatus = useContext(WebsiteStatusContext);

  if (!permissions.includes('conversations_and_marks')) {
    return <PermissionDeniedPage title={TITLE} permission="conversations and marks" />;
  }
  if (websiteStatus === 'not-found') return <WebsiteNotFoundPage title={TITLE} />;

  return <LeadsPage />;
}

function LeadsPage() {
  const { websiteId } = Route.useParams();
  const { page = 1 } = Route.useSearch();
  const navigate = useNavigate();
  const leads = useLeads(websiteId, { page });

  return (
    <>
      <header className={styles.header}>
        <PageHeading documentTitle={TITLE}>{TITLE}</PageHeading>
        <p className={styles.caption}>
          {leads.data === undefined
            ? ''
            : `${leads.data.total} ${leads.data.total === 1 ? 'lead' : 'leads'}`}
        </p>
      </header>
      {leads.isError ? (
        <DangerAlert
          title="Leads didn't load"
          action={<Button onClick={() => void leads.refetch()}>Try again</Button>}
        >
          Groundly couldn&apos;t reach the server. Check your connection and try again.
        </DangerAlert>
      ) : leads.isPending ? (
        <LoadingArea />
      ) : leads.data.total === 0 ? (
        <div className={styles.empty}>
          <h2>No leads yet</h2>
          <p>
            When a visitor leaves their details in the chat, or taps your email, phone number or
            another contact link the assistant shows, they appear here.
          </p>
        </div>
      ) : (
        <>
          <ul className={styles.list}>
            {leads.data.items.map((lead) => (
              <LeadRow key={lead.id} websiteId={websiteId} lead={lead} />
            ))}
          </ul>
          <Pagination
            page={leads.data.page}
            pageSize={leads.data.pageSize}
            total={leads.data.total}
            onPageChange={(next) => void navigate({ to: '.', search: searchForPage(next) })}
          />
        </>
      )}
    </>
  );
}

function LeadRow({ websiteId, lead }: { websiteId: string; lead: LeadListItem }) {
  const hasDetails = lead.contactType === 'visitor_details';
  return (
    <li className={styles.item}>
      <div className={styles.top}>
        {hasDetails ? (
          <strong className={styles.name}>{lead.name}</strong>
        ) : (
          <span className={styles.name}>Visitor</span>
        )}
        <StatusBadge tone={hasDetails ? 'success' : 'neutral'} icon={CircleCheck}>
          {leadSourceLabel(lead.contactType)}
        </StatusBadge>
        <time className={styles.time} dateTime={lead.createdAt}>
          {formatFullDateTime(lead.createdAt)}
        </time>
      </div>
      {hasDetails ? (
        <p className={styles.contacts}>
          {lead.email !== null ? <a href={`mailto:${lead.email}`}>{lead.email}</a> : null}
          {lead.phone !== null ? (
            <a href={`tel:${lead.phone.replace(/[^\d+]/g, '')}`}>{lead.phone}</a>
          ) : null}
        </p>
      ) : null}
      {lead.message !== null ? <p className={styles.message}>“{lead.message}”</p> : null}
      <p className={styles.context}>
        {lead.firstVisitorMessage !== null ? (
          <span className={styles.asked}>Asked: {lead.firstVisitorMessage}</span>
        ) : null}
        <Link
          to="/websites/$websiteId/conversations/$conversationId"
          params={{ websiteId, conversationId: lead.conversationId }}
        >
          View conversation
        </Link>
      </p>
    </li>
  );
}
